import { map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { Tag } from '../_models/tag';
import { AppState } from '../_reducers';
import { FilterElement } from '../../../assets/data/enum/filterElement';
import { loadTags } from '../_actions/tag';

@Injectable()
export class TagService {
  private store = inject(Store<AppState>);
  private http = inject(HttpClient);

  private apiBaseUrl = environment.apiBaseUrl;

  internalFarmListSubject: BehaviorSubject<Tag[]> = new BehaviorSubject<Tag[]>(null);
  selectedInternalFarmSubject: BehaviorSubject<Tag[]> = new BehaviorSubject<Tag[]>([]);
  selectedInternalTagNamesSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  internalFarmList$ = this.internalFarmListSubject.asObservable();
  selectedInternalFarm$ = this.selectedInternalFarmSubject.asObservable();
  selectedInternalTagNames$ = this.selectedInternalTagNamesSubject.asObservable();

  async initTags(): Promise<void> {
    this.store.dispatch(loadTags());

    const localFilterTags: Tag[] = JSON.parse(localStorage.getItem(FilterElement.filterTags));
    const selectedInternalFarm: string[] = localFilterTags?.map((t) => t.tagName) || [];

    const tags = await firstValueFrom(
      this.internalFarmList$.pipe(filter((tags): tags is Tag[] => tags !== null))
    );

    const filtered = tags.filter((tag) => selectedInternalFarm.includes(tag.tagName));
    this.selectedInternalFarmSubject.next(filtered);
    this.selectedInternalTagNamesSubject.next(filtered.map((t) => t.tagName));
  }

  getTags(): Observable<Tag[]> {
    return this.http.get<Tag[]>(`${this.apiBaseUrl}api/private/v1/tags`).pipe(
      map((response) => {
        const sorted = [...(response ?? [])].sort((a, b) =>
          (a?.tagName ?? '').toUpperCase().localeCompare((b?.tagName ?? '').toUpperCase())
        );

        this.internalFarmListSubject.next(sorted);

        return sorted;
      })
    );
  }

  // Setter selected
  setSelectedInternalTagNames(selectedTagNames: string[]): void {
    const allTags = this.internalFarmListSubject.getValue() ?? [];
    const selectedTags = allTags?.filter((t) => selectedTagNames.includes(t.tagName)) || [];

    // Update subjects
    this.selectedInternalFarmSubject.next(selectedTags);
    this.selectedInternalTagNamesSubject.next(selectedTagNames);

    // Save to localStorage for persistence
    localStorage.setItem(FilterElement.filterTags, JSON.stringify(selectedTags));
  }

  saveTag(tagItem: Tag): Observable<Tag> {
    const tag = { ...tagItem };
    delete tag.lastUpdated;
    delete tag.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    tag.companyId = companySelected ? companySelected.id : null;

    if (tag.id) {
      return this.http
        .put<Tag>(this.apiBaseUrl + 'api/private/v1/tags/' + tag.id, tag)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Tag>(this.apiBaseUrl + 'api/private/v1/tags/', tag)
        .pipe(map((response) => response));
    }
  }

  deleteTag(tagId: number): Observable<Tag> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/tags/' + tagId).pipe(
      map((response) => {
        if (response) {
          return { id: tagId } as Tag;
        } else {
          return null;
        }
      })
    );
  }

  getTagQuery(prependAmpersand = true): string {
    const selectedTags = this.selectedInternalTagNamesSubject.getValue() ?? [];
    if (selectedTags.length === 0) {
      return '';
    }
    return `${prependAmpersand ? '&' : ''}tags=${selectedTags.map(encodeURIComponent).join(',')}`;
  }
}
